import { track as libTrack, type TrackingEvent } from '@nhst/tracking-lib';
import { toMilliseconds } from '~/utils/trackingMappers';

type Action = 'click' | 'scroll';
type Component = NonNullable<TrackingEvent['component']>;
type Content = NonNullable<TrackingEvent['content']>;
type Attributes = NonNullable<TrackingEvent['attributes']>;

/**
 * Single typed boundary to `@nhst/tracking-lib`'s `track()`. Centralises the
 * one-time cast required when CPP-shaped payloads meet the library's wider
 * envelope type, so emission sites never need their own `as any`.
 */
export function trackContract (event: TrackingEvent): void {
  libTrack(event);
}

export interface BindingInput {
  eventName?: string;
  // Stable snake_case identifier for scroll markers for new tracking.
  slot?: string;
  component?: {
    zone?: string;
    element?: string;
    element_id?: string;
  };
  attributes?: Record<string, unknown>;
  /*
   * Contract-only content fields. Read solely by toContractEvent; the legacy
   * fan-out (toLegacyAction → nhstDataLayer) never touches this block, so cards
   * can add data-contract content fields without changing the frozen legacy shape.
   */
  content?: Record<string, unknown>;
}

export type ContractEvent = Pick<TrackingEvent, 'component' | 'content' | 'attributes'>;

const COMPONENT_PROPERTY_KEYS = [
  'element_position',
  'element_text',
  'link_url',
  'teaser_type',
  'row_index',
  'row_layout',
  'stream_id'
] as const;

const CONTENT_TOP_LEVEL_KEYS = ['id', 'title', 'type'] as const;

const CONTENT_PROPERTY_KEYS = [
  'canonical_slug',
  'paid',
  'publication',
  'kicker',
  'badge',
  'original_title',
  'publish_timestamp',
  'modify_timestamp',
  'category',
  'topic',
  'tags'
] as const;

const TIMESTAMP_KEYS = ['publish_timestamp', 'modify_timestamp'] as const;

export function toContractEvent (
  binding: BindingInput,
  action?: Action
): ContractEvent {
  const attrs = binding.attributes ?? {};
  const componentInput = binding.component ?? {};
  /*
   * Content is sourced from attributes (legacy-shared) merged with the contract-only
   * content block. binding.content wins on key clash. component scan stays on attrs.
   */
  const contentSource = { ...attrs, ...(binding.content ?? {}) };

  // component
  const componentProperties: Record<string, unknown> = {};
  for (const k of COMPONENT_PROPERTY_KEYS) {
    if (attrs[k] !== undefined) { componentProperties[k] = attrs[k]; }
  }

  /*
   * Scroll markers carry a stable snake_case `slot` identifier.Guarded like every other property above:
   * click bindings omit slot, so this is a no-op for them and never writes `slot: undefined` onto
   * non-scroll events.
   */
  if (binding.slot) { componentProperties.slot = binding.slot; }

  /*
   * Omit undefined/empty keys so the library's fillWithNulls sets them to null per
   * the contract. `?? ''` defeats the library default and emits empty strings on
   * the wire.
   */
  let component: Component | undefined;
  if (componentInput.zone || componentInput.element || componentInput.element_id || Object.keys(componentProperties).length > 0) {
    component = {
      ...(componentInput.zone && { zone: componentInput.zone }),
      ...(componentInput.element && { element: componentInput.element }),
      ...(componentInput.element_id && { element_id: componentInput.element_id })
    } as Component;
    if (Object.keys(componentProperties).length > 0) {
      component.properties = componentProperties;
    }
  }

  /*
   * content — filter undefined, null AND empty strings; callers (e.g. teaser data) often
   * pass '' or null for absent fields (e.g. badge: features.badge || null), which would
   * otherwise defeat the library's fillWithNulls and emit them on the wire.
   */
  const contentTop: Record<string, unknown> = {};
  for (const k of CONTENT_TOP_LEVEL_KEYS) {
    if (contentSource[k]) { contentTop[k] = contentSource[k]; }
  }
  const contentProperties: Record<string, unknown> = {};
  for (const k of CONTENT_PROPERTY_KEYS) {
    if (contentSource[k] === undefined || contentSource[k] === null || contentSource[k] === '') { continue; }
    if ((TIMESTAMP_KEYS as readonly string[]).includes(k)) {
      const ms = toMilliseconds(contentSource[k] as number);
      if (ms !== undefined) { contentProperties[k] = ms; }
    } else if (typeof contentSource[k] === 'object' && contentSource[k] !== null && !Array.isArray(contentSource[k])) {
      /*
       * Some sources spread raw objects (e.g. dn-teaser-card-old, DnRelated) whose
       * publication/topic are { name, ... }. The contract requires these as strings —
       * extract .name; if absent, omit rather than emit a raw object.
       */
      const name = (contentSource[k] as { name?: unknown }).name;
      if (name !== undefined && name !== '') { contentProperties[k] = name; }
    } else {
      contentProperties[k] = contentSource[k];
    }
  }
  let content: Content | undefined;
  if (Object.keys(contentTop).length > 0 || Object.keys(contentProperties).length > 0) {
    content = {
      ...(!!contentTop.id && { id: contentTop.id as string }),
      ...(!!contentTop.title && { title: contentTop.title as string }),
      ...(!!contentTop.type && { type: contentTop.type as string })
    } as Content;
    if (Object.keys(contentProperties).length > 0) {
      content.properties = contentProperties;
    }
  }

  /*
   * attributes — the v2 contract narrows attributes to action | edition_id | name
   * (additionalProperties: false; event_name removed). For interaction events the
   * shaper only carries the verb; edition_id / name are page_view-only and set in
   * buildPageLoadPayload. The block is omitted entirely when empty.
   */
  const attributes: Attributes = {};
  if (action) { attributes.action = action; }

  const out: ContractEvent = {};
  if (component) { out.component = component; }
  if (content) { out.content = content; }
  if (Object.keys(attributes).length > 0) { out.attributes = attributes; }
  return out;
}

/**
 * Fills `component.properties` with the DOM-derived fields the legacy `constructEventInfo`
 * already computes (element_position, row_index, row_layout, stream_id, teaser_type,
 * link_url) when the binding didn't pre-compute them. Binding-provided values win.
 * Writes ONLY to `component.properties` — never to `attributes`. The `action` arg is the
 * legacy `eventInfo.action` bag (legacy-named keys). Mutates and returns `contract`.
 * Exported for unit testing.
 */
export function addComponentProps (contract: ContractEvent, action: Record<string, unknown>): ContractEvent {
  const componentProps: Record<string, unknown> = {
    element_position: action.elementPosition,
    row_index: action.rowIndex,
    row_layout: action.rowLayout,
    stream_id: action.streamId,
    teaser_type: action.teaserType,
    link_url: action.linkUrl
  };
  // Binding-provided component.properties take precedence over the DOM-derived values.
  const merged = { ...componentProps, ...(contract.component?.properties ?? {}) };
  for (const k of Object.keys(merged)) {
    if (merged[k] === undefined || merged[k] === '') { delete merged[k]; }
  }
  if (contract.component && Object.keys(merged).length > 0) {
    contract.component.properties = merged;
  }
  return contract;
}

/**
 * Converts a legacy {component, elementName, elementId, elementPosition, teaserType,
 * rowIndex, rowLayout, linkUrl, streamId, elementText, ...content} bag to the
 * binding shape, then routes through toContractEvent. Used by the third-party
 * bridge and the fanOutEventById composable helper where the call site has
 * legacy-shaped input.
 */
export interface LegacyBag {
  eventName?: string;
  action?: Action;
  component?: string;
  element?: string;
  elementName?: string;
  elementId?: string;
  elementPosition?: string;
  teaserType?: string;
  rowIndex?: number;
  rowLayout?: string;
  linkUrl?: string;
  streamId?: string;
  elementText?: string;
  [key: string]: unknown;
}

export function legacyToContract (bag: LegacyBag): ContractEvent {
  const {
    eventName,
    action,
    component,
    element,
    elementName,
    elementId,
    elementPosition,
    teaserType,
    rowIndex,
    rowLayout,
    linkUrl,
    streamId,
    elementText,
    ...rest
  } = bag;

  const binding: BindingInput = {
    eventName,
    component: {
      zone: component,
      element: element ?? elementName,
      element_id: elementId
    },
    attributes: {
      element_position: elementPosition,
      element_text: elementText,
      teaser_type: teaserType,
      row_index: rowIndex,
      row_layout: rowLayout,
      stream_id: streamId,
      link_url: linkUrl,
      ...rest
    }
  };

  return toContractEvent(binding, action);
}
